import { fromJS, Map, Record, Set } from '@gonfalon/immutable-shim/immutableShim';
import type { ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';

import { allowDecision } from '#utils/access/allowDecision';
import { createAccessDecision } from '#utils/access/records/AccessDecision';
import type { AccessChecks } from '#utils/access/types';
import type { Link } from '#utils/linkUtils';
import type { Member } from '#utils/members/records/Member';

import { type TeamCustomRoleSummary } from './TeamCustomRoleSummary';
import { createTeamCustomRoleSummary } from './TeamCustomRoleSummary';
import { type TeamMaintainerSummary } from './TeamMaintainerSummary';
import { createTeamMaintainerSummary } from './TeamMaintainerSummary';
import type { TeamMemberSummary } from './TeamMemberSummary';
import { createTeamMemberSummary } from './TeamMemberSummary';
import { type TeamProjectSummary } from './TeamProjectSummary';

export type TeamProps = {
  _access?: AccessChecks;
  _links: ImmutableMap<{
    self: Link;
    parent: Link;
  }>;
  _idpSynced?: boolean;
  roles: TeamCustomRoleSummary;
  description?: string;
  key: string;
  members?: TeamMemberSummary;
  name: string;
  projects?: TeamProjectSummary;
  maintainers: TeamMaintainerSummary;
  roleAttributes?: ImmutableMap<{ [key: string]: Set<string> }>;
};

export class Team extends Record<TeamProps>({
  _access: undefined,
  _idpSynced: undefined,
  _links: Map(),
  roles: createTeamCustomRoleSummary(),
  description: '',
  key: '',
  members: createTeamMemberSummary(),
  name: '',
  projects: undefined,
  maintainers: createTeamMaintainerSummary(),
  roleAttributes: undefined,
}) {
  checkAccess({ profile }: { profile: Member }) {
    const access = this._access;

    if (profile.isReader()) {
      return () => createAccessDecision({ isAllowed: false, appliedRoleName: 'Reader' });
    }

    if (profile.isWriter()) {
      return () => createAccessDecision({ isAllowed: false, appliedRoleName: 'Writer' });
    }

    if (profile.isAdminOrOwner() || !access || !access.get('denied')) {
      return allowDecision;
    }

    return (action?: string) => {
      const deniedAction = access.get('denied').find((v) => v.get('action') === action);
      if (deniedAction) {
        const reason = deniedAction.get('reason');
        const roleName = reason && reason.get('role_name');
        return createAccessDecision({
          isAllowed: false,
          appliedRoleName: roleName,
        });
      }
      return createAccessDecision({ isAllowed: true });
    };
  }

  getMaintainerIDs() {
    const maintainerIds = (this.maintainers?.items || []).map((m) => m._id);
    return Set<string>(maintainerIds);
  }

  getDisplayName() {
    return this.name;
  }
}

export function createTeam(props: Partial<TeamProps> = {}) {
  let team = props instanceof Team ? props : new Team(fromJS(props));
  team = team.withMutations((t) => {
    t.update('roles', (customRoleSummary) => createTeamCustomRoleSummary(customRoleSummary));
    t.update('members', (memberSummary) => createTeamMemberSummary(memberSummary));
    t.update('maintainers', (teamMaintainerSummary) => createTeamMaintainerSummary(teamMaintainerSummary));
  });
  return team;
}
